import { EditOutlined, MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import { useCreatePlanContext } from 'contexts/CreatePlanContextProvider'
import React, { useEffect, useState } from 'react'
import type { VariableAllocationRow } from 'types/comp-allocations'
import { RowType } from 'types/comp-allocations'
import type { ExpandedTarget } from 'types/components'
import { currencyFormatter } from 'utils/formatting'
import AllocationEditDialog from './AllocationEditDialog'

interface FlatRow {
  row: VariableAllocationRow
  groupLabel: string
  tableIndex: number
  rowIndex: number
}

interface PlanAllocationTablesProps {
  reviewOnly?: boolean
}

const PlanAllocationTables: React.FC<PlanAllocationTablesProps> = ({ reviewOnly = false }) => {
  const theme = useTheme()

  const { compensationTemplate, templateComponentsInPlan, setTemplateComponentsInPlan, plan, setPlan } =
    useCreatePlanContext()

  useEffect(() => {
    if (plan.variableAllocations.length === 0 && compensationTemplate !== undefined && compensationTemplate !== null) {
      plan.variableAllocations = compensationTemplate.template.variableAllocations
      setPlan(plan)
      setTemplateComponentsInPlan(compensationTemplate.template.variableAllocations)
    }
  }, [[templateComponentsInPlan, plan]])

  const [editingRow, setEditingRow] = useState<FlatRow | null>(null)

  const baseSalary = plan.baseSalary
  const variablePercent = plan.variablePercent
  const periodProration = plan.periodProration

  const allocations =
    plan.variableAllocations.length > 0
      ? plan.variableAllocations
      : compensationTemplate != undefined && compensationTemplate != null
        ? compensationTemplate.template.variableAllocations
        : []

  // IMPORTANT: compensationTemplate.targets is TargetDTO[], NOT ExpandedTarget[]
  // The mapping wraps each TargetDTO as { target: TargetDTO, component: Component }
  const expandedTargets =
    compensationTemplate != undefined && compensationTemplate != null
      ? compensationTemplate.targets.map((target) => {
          const component = compensationTemplate.components.find((component) => component.id === target.componentId)
          return {
            target,
            component,
          } as ExpandedTarget
        })
      : []

  // Flatten all rows with group label
  const allRows: FlatRow[] = allocations.flatMap((table, tableIndex) =>
    table.rows.map((row, rowIndex) => ({
      row,
      groupLabel: table.label,
      tableIndex,
      rowIndex,
    }))
  )

  const visibleRows = reviewOnly ? allRows.filter((item) => !item.row.disabled) : allRows

  const updateRowProperty = (
    tableIndex: number,
    rowIndex: number,
    updateFunction: (row: VariableAllocationRow) => void
  ) => {
    if (
      plan &&
      plan.variableAllocations &&
      plan.variableAllocations[tableIndex] &&
      plan.variableAllocations[tableIndex].rows &&
      plan.variableAllocations[tableIndex].rows[rowIndex]
    ) {
      const newPlan = { ...plan }
      const row = newPlan.variableAllocations[tableIndex].rows[rowIndex]
      updateFunction(row)
      setPlan(newPlan)
      setTemplateComponentsInPlan(newPlan.variableAllocations)
    }
  }

  const handleSave = (
    tableIndex: number,
    rowIndex: number,
    updates: {
      allocationPercentage: number
      defaultPayout: number
      achievementMultiplier: string
      disabled: boolean
    }
  ) => {
    updateRowProperty(tableIndex, rowIndex, (row) => {
      row.allocationPercentage = updates.allocationPercentage
      row.defaultPayout = updates.defaultPayout
      row.achievementMultiplier = updates.achievementMultiplier
      row.disabled = updates.disabled
    })
  }

  const toggleDisabled = (tableIndex: number, rowIndex: number) => {
    updateRowProperty(tableIndex, rowIndex, (row) => {
      if (row.disabled == undefined || row.disabled == null) {
        row.disabled = false
      }
      row.disabled = !row.disabled
    })
  }

  const getRowTargets = (row: VariableAllocationRow): ExpandedTarget[] => {
    return expandedTargets?.filter((target) => row.componentIds?.includes(target.target.componentId)) ?? []
  }

  // Calculate total payout for footer
  const totalPayout = visibleRows
    .filter((item) => !item.row.disabled)
    .reduce((acc, item) => {
      return acc + baseSalary * item.row.allocationPercentage * variablePercent * periodProration
    }, 0)

  return (
    <>
      <Paper sx={{ width: '100%' }}>
        <TableContainer>
          <Table stickyHeader size="small" aria-label="allocations-table" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ minWidth: 150 }}>
                  <strong>Allocation</strong>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <strong>Group</strong>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }} align="right">
                  <strong>Allocation %</strong>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }} align="right">
                  <strong>On-Target</strong>
                </TableCell>
                {!reviewOnly && (
                  <TableCell sx={{ minWidth: 100 }} align="center">
                    <strong>Actions</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((item) => {
                const isDisabled = item.row.disabled ?? false
                const allocationPercent = ((item.row.allocationPercentage ?? 0) * 100).toFixed(2)
                const onTargetPayout = baseSalary * item.row.allocationPercentage * variablePercent * periodProration

                return (
                  <TableRow key={`${item.tableIndex}-${item.rowIndex}`} sx={{ opacity: isDisabled ? 0.4 : 1 }}>
                    <TableCell>
                      <Typography sx={{ textDecoration: isDisabled ? 'line-through' : 'none' }}>
                        {item.row.label}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.groupLabel}</TableCell>
                    <TableCell align="right">{!isDisabled && `${allocationPercent}%`}</TableCell>
                    <TableCell align="right">
                      {!isDisabled && item.row.rowType !== RowType.DISPLAY_ROW && (
                        <Typography fontWeight="bold">{currencyFormatter.format(onTargetPayout)}</Typography>
                      )}
                    </TableCell>
                    {!reviewOnly && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {!isDisabled && (
                            <IconButton size="small" onClick={() => setEditingRow(item)} sx={{ color: 'primary.main' }}>
                              <EditOutlined />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => toggleDisabled(item.tableIndex, item.rowIndex)}
                            style={{ color: 'inherit' }}
                          >
                            {isDisabled ? (
                              <PlusCircleOutlined style={{ color: theme.palette.primary.main }} />
                            ) : (
                              <MinusCircleOutlined style={{ color: theme.palette.warning.main }} />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
              {/* Footer row with On-Target Total */}
              <TableRow>
                <TableCell colSpan={3} align="right">
                  <Typography variant="h6" fontWeight="bold">
                    On-Target Total:
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="h6" fontWeight="bold">
                    {currencyFormatter.format(totalPayout)}
                  </Typography>
                </TableCell>
                {!reviewOnly && <TableCell />}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Dialog */}
      {editingRow && (
        <AllocationEditDialog
          open={!!editingRow}
          onClose={() => setEditingRow(null)}
          row={editingRow.row}
          groupLabel={editingRow.groupLabel}
          baseSalary={baseSalary}
          variablePercent={variablePercent}
          periodProration={periodProration}
          rowTargets={getRowTargets(editingRow.row)}
          onSave={(updates) => handleSave(editingRow.tableIndex, editingRow.rowIndex, updates)}
        />
      )}
    </>
  )
}

export default PlanAllocationTables
